import { type ReactNode, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ProjectCombobox } from "../projects/ProjectCombobox";
import { ModelPicker } from "../shared/ModelPicker";
import type { AutonomyLevel, TaskPriority } from "../../lib/types";
import { AUTONOMY_LEVELS, TASK_PRIORITIES } from "../../lib/types";
import { cn } from "../../lib/cn";
import {
  models as modelsApi,
  projects as projectsApi,
  TASK_TIERS,
  type TaskTier,
} from "../../lib/api";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { formatCost, formatTokens } from "../../lib/format";

export interface TaskFieldsValue {
  projectId: string;
  title: string;
  description: string;
  prompt: string;
  autonomy: AutonomyLevel | "";
  model: string;
  priority: TaskPriority | "";
  specMode: boolean;
  tier: TaskTier;
  acceptanceCriteria: string;
}

interface Props {
  value: TaskFieldsValue;
  onChange: (next: Partial<TaskFieldsValue>) => void;
  projectLocked?: boolean; // edit mode: project ID is immutable
  autoFocusField?: "title" | "prompt";
  // The CLI a task actually ran on. After a run the runner normalizes `model`
  // to the bare id and records the provider here, so this re-seeds the Provider
  // dropdown for tasks that ran on a non-default provider (e.g. a Codex fallback).
  agentType?: string | null;
}

export function TaskFields({
  value,
  onChange,
  projectLocked,
  autoFocusField = "title",
  agentType,
}: Props) {
  const debouncedPrompt = useDebouncedValue(value.prompt, 400);
  const skills = useQuery({
    queryKey: ["project-skills", value.projectId],
    queryFn: () => projectsApi.skills(value.projectId),
    enabled: !!value.projectId,
    staleTime: 30_000,
  });

  // Don't bounce a request if the project isn't picked yet. The estimator
  // tolerates an empty prompt — it falls back to its low-confidence rate.
  const estimate = useQuery({
    queryKey: ["project-cost-estimate", value.projectId, debouncedPrompt],
    queryFn: () => projectsApi.estimateCost(value.projectId, debouncedPrompt),
    enabled: !!value.projectId,
    staleTime: 15_000,
  });

  const onPickSkill = (skillId: string) => {
    const found = skills.data?.find((s) => s.id === skillId);
    if (!found) return;
    const separator = value.prompt.trim() ? "\n\n---\n\n" : "";
    onChange({ prompt: `${found.body.trim()}${separator}${value.prompt}` });
  };

  const skillOptions = useMemo(() => skills.data ?? [], [skills.data]);

  // Model catalog from the backend + the current project's CLI binary so we
  // can filter to just the relevant choices.
  const modelCatalog = useQuery({
    queryKey: ["models"],
    queryFn: () => modelsApi.list(),
    staleTime: 5 * 60_000,
  });
  const focusedProject = useQuery({
    queryKey: ["project", value.projectId],
    queryFn: () => projectsApi.get(value.projectId),
    enabled: !!value.projectId,
    staleTime: 30_000,
  });
  // The `model` field carries provider + model + effort as one
  // `[<cli>:]<model>[@<effort>]` string; ModelPicker owns the three dropdowns.
  const projectCli = focusedProject.data?.cli_binary;

  return (
    <div className="space-y-4">
      <Field label="Project">
        {projectLocked ? (
          <div
            className={cn(
              "h-10 px-3 inline-flex items-center bg-bg-inset rounded-md",
              "text-small text-text-tertiary",
            )}
          >
            <span className="text-mono">{value.projectId}</span>
          </div>
        ) : (
          <ProjectCombobox
            value={value.projectId}
            onChange={(v) => onChange({ projectId: v })}
          />
        )}
      </Field>

      <Field label="Title" required>
        <input
          className={inputClasses}
          placeholder="What do you want the agent to do?"
          value={value.title}
          onChange={(e) => onChange({ title: e.target.value })}
          autoFocus={autoFocusField === "title"}
        />
      </Field>

      <Field label="Description" hint="optional">
        <textarea
          className={cn(inputClasses, "min-h-[240px] py-2 resize-y")}
          placeholder="A short note for context"
          value={value.description}
          onChange={(e) => onChange({ description: e.target.value })}
        />
      </Field>

      <Field label="Acceptance criteria" hint="optional">
        <textarea
          className={cn(inputClasses, "min-h-[80px] py-2 resize-y")}
          placeholder="What done looks like — goes into the agent's brief"
          value={value.acceptanceCriteria}
          onChange={(e) => onChange({ acceptanceCriteria: e.target.value })}
        />
      </Field>

      <Field
        label="Prompt"
        hint="optional — the title is used when blank"
        extra={
          <span className="inline-flex items-center gap-2">
            {skillOptions.length > 0 && (
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) onPickSkill(e.target.value);
                  e.target.value = "";
                }}
                aria-label="Prepend a project skill"
                className={cn(
                  "h-7 px-1.5 text-small bg-bg-inset rounded-sm",
                  "text-text-tertiary border border-transparent hover:border-border-default",
                  "outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1",
                )}
              >
                <option value="">+ skill…</option>
                {skillOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            )}
          </span>
        }
      >
        <textarea
          className={cn(
            "w-full px-3 py-2 bg-bg-inset rounded-md min-h-[320px]",
            "border border-transparent hover:border-border-default focus:border-border-strong",
            "font-mono text-[13px] leading-relaxed text-text-primary placeholder:text-text-quaternary",
            "outline-none resize-y transition-colors duration-fast ease-out",
          )}
          placeholder="The exact instruction passed to the CLI. Be specific."
          value={value.prompt}
          onChange={(e) => onChange({ prompt: e.target.value })}
          autoFocus={autoFocusField === "prompt"}
        />
      </Field>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <ModelPicker
          value={value.model}
          onChange={(model) => onChange({ model })}
          models={modelCatalog.data?.models ?? []}
          defaultProvider={projectCli}
          agentType={agentType}
        />
      </div>

      <div className="grid grid-cols-4 gap-3">
        <Field label="Tier">
          <select
            className={cn(inputClasses, "h-9")}
            value={value.tier}
            onChange={(e) => onChange({ tier: e.target.value as TaskTier })}
            title="Size: picks the model and the token budget"
          >
            {TASK_TIERS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Priority">
          <select
            className={cn(inputClasses, "h-9")}
            value={value.priority}
            onChange={(e) =>
              onChange({ priority: (e.target.value as TaskPriority) || "" })
            }
          >
            <option value="">—</option>
            {TASK_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Autonomy">
          <select
            className={cn(inputClasses, "h-9")}
            value={value.autonomy}
            onChange={(e) =>
              onChange({ autonomy: (e.target.value as AutonomyLevel) || "" })
            }
          >
            <option value="">use project default</option>
            {AUTONOMY_LEVELS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Spec mode">
          <label className="flex items-center gap-2 h-9">
            <input
              type="checkbox"
              checked={value.specMode}
              onChange={(e) => onChange({ specMode: e.target.checked })}
            />
          </label>
        </Field>
      </div>

      {value.projectId && (
        <CostEstimateRow
          tokens={estimate.data?.tokens}
          cost={estimate.data?.cost_usd}
          loading={estimate.isFetching && !estimate.data}
        />
      )}
    </div>
  );
}

function CostEstimateRow({
  tokens,
  cost,
  loading,
}: {
  tokens?: number;
  cost?: string;
  loading: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 px-3 py-2 rounded-md",
        "bg-bg-inset border border-border-subtle",
      )}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-caption text-text-tertiary">Estimated</span>
        {loading ? (
          <span className="text-mono text-text-quaternary">predicting…</span>
        ) : (
          <span className="text-small text-text-secondary truncate">
            {formatTokens(tokens ?? 0)} tokens · {formatCost(cost ?? "0")}
          </span>
        )}
      </div>
    </div>
  );
}

const inputClasses = cn(
  "w-full h-10 px-3 bg-bg-inset rounded-md",
  "border border-transparent hover:border-border-default focus:border-border-strong",
  "text-body text-text-primary placeholder:text-text-quaternary outline-none",
  "transition-colors duration-fast ease-out",
);

function Field({
  label,
  hint,
  extra,
  required,
  children,
}: {
  label: string;
  hint?: string;
  extra?: ReactNode;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-caption text-text-tertiary">
          {required && (
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ background: "var(--accent)" }}
              aria-label="required"
            />
          )}
          {label}
          {hint && (
            <span className="text-text-quaternary normal-case tracking-normal">
              {hint}
            </span>
          )}
        </span>
        {extra}
      </span>
      {children}
    </label>
  );
}
